/**
 * AttachmentCard - Mini card component for displaying individual file attachments
 * Shows file name, size, action, and user decision
 */

import { File, CheckCircle, XCircle, AlertTriangle, Shield, ArrowRight, Download } from 'lucide-react';
import { downloadFile } from '../utils/fileDownload';

export type AttachmentResult = {
  file_id: string | number;
  name?: string;
  filename?: string;
  action?: string; // ALLOW, WARN, AMEND, BLOCK
  reason?: string;
  summary?: string;
  size?: number;
  filter_result?: {
    action?: string;
    reason?: string;
    resource_violations?: Record<string, unknown>;
  };
};

export type AttachmentCardProps = {
  attachment: AttachmentResult;
  decision?: string; // approve, deny, use_original (from user interaction)
  isAmended?: boolean; // Whether this is an amended file
};

/**
 * Format file size to human-readable format
 */
function formatFileSize(bytes?: number): string {
  if (!bytes || bytes === 0) return '';

  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${size.toFixed(1)} ${units[unitIndex]}`;
}

/**
 * Get action icon and color based on filter action
 */
function getActionIcon(action?: string) {
  const actionUpper = (action || '').toUpperCase();

  switch (actionUpper) {
    case 'ALLOW':
      return { icon: <CheckCircle size={16} />, color: '#10b981', label: 'Allowed' };
    case 'WARN':
      return { icon: <AlertTriangle size={16} />, color: '#eab308', label: 'Warning' };
    case 'AMEND':
      return { icon: <Shield size={16} />, color: '#f59e0b', label: 'Amended' };
    case 'BLOCK':
      return { icon: <XCircle size={16} />, color: '#ef4444', label: 'Blocked' };
    default:
      return { icon: <File size={16} />, color: '#64748b', label: 'Unknown' };
  }
}

/**
 * Get decision badge content
 */
function getDecisionBadge(decision?: string, action?: string) {
  if (!decision) return null;

  const actionUpper = (action || '').toUpperCase();

  if (actionUpper === 'AMEND') {
    switch (decision) {
      case 'approve':
        return { label: '✓ Used amended', color: '#10b981', bg: '#d1fae5' };
      case 'deny':
        return { label: '✗ Removed', color: '#ef4444', bg: '#fee2e2' };
      case 'use_original':
        return { label: '↩ Kept original', color: '#6366f1', bg: '#e0e7ff' };
    }
  } else if (actionUpper === 'WARN') {
    switch (decision) {
      case 'approve':
        return { label: '✓ Included', color: '#10b981', bg: '#d1fae5' };
      case 'deny':
        return { label: '✗ Removed', color: '#ef4444', bg: '#fee2e2' };
    }
  }

  return null;
}

export function AttachmentCard({ attachment, decision, isAmended }: AttachmentCardProps) {
  const fileName = attachment.name || attachment.filename || `File ${attachment.file_id}`;
  const action = attachment.action || attachment.filter_result?.action;
  const reason = attachment.reason || attachment.summary || attachment.filter_result?.reason;
  const sizeStr = formatFileSize(attachment.size);

  const { icon, color, label } = getActionIcon(action);
  const decisionBadge = getDecisionBadge(decision, action);

  return (
    <div
      style={{
        border: `1px solid #e5e7eb`,
        borderRadius: '8px',
        padding: '12px',
        marginBottom: '8px',
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
      }}
      className="dark:bg-gray-800 dark:border-gray-700"
    >
      {/* Header: Icon + File name + Size + Download */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ color, display: 'flex', alignItems: 'center' }}>{icon}</div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
            className="text-gray-900 dark:text-gray-100"
          >
            {fileName}
            {isAmended && (
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 500,
                  padding: '2px 6px',
                  borderRadius: '4px',
                }}
                className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300"
              >
                AMENDED
              </span>
            )}
          </div>
          {sizeStr && (
            <div style={{ fontSize: '12px' }} className="text-gray-600 dark:text-gray-400">
              {sizeStr}
            </div>
          )}
        </div>
        <button
          onClick={() => downloadFile(attachment.file_id, fileName)}
          style={{
            padding: '6px',
            borderRadius: '6px',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          className="bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
          title="Download file"
        >
          <Download size={16} className="text-gray-700 dark:text-gray-300" />
        </button>
      </div>

      {/* Action badge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
        <span
          style={{
            padding: '3px 10px',
            borderRadius: '6px',
            backgroundColor: color + '15',
            color: color,
            fontWeight: 600,
            fontSize: '12px',
          }}
        >
          {label}
        </span>
      </div>

      {/* Reason */}
      {reason && (
        <div style={{ fontSize: '12px', marginTop: '-4px' }} className="text-gray-600 dark:text-gray-400 italic">
          {reason}
        </div>
      )}

      {/* User decision badge */}
      {decisionBadge && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            marginTop: '4px',
            paddingTop: '8px',
            borderTop: '1px solid #e5e7eb',
          }}
        >
          <ArrowRight size={14} color="#9ca3af" />
          <span
            style={{
              padding: '3px 10px',
              borderRadius: '4px',
              backgroundColor: decisionBadge.bg,
              color: decisionBadge.color,
              fontWeight: 600,
              fontSize: '12px',
            }}
          >
            {decisionBadge.label}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * AttachmentList - Container for multiple attachment cards
 */
export type AttachmentListProps = {
  attachments: AttachmentResult[];
  decisions?: Record<string, string>; // file_id -> decision mapping
  amendedFileIds?: (string | number)[];
};

export function AttachmentList({ attachments, decisions = {}, amendedFileIds = [] }: AttachmentListProps) {
  if (!attachments || attachments.length === 0) {
    return (
      <div style={{ fontSize: '13px', fontStyle: 'italic' }} className="text-gray-600 dark:text-gray-400">
        No attachments
      </div>
    );
  }

  const amendedSet = new Set(amendedFileIds.map(String));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      {attachments.map((att, idx) => {
        const fileIdStr = String(att.file_id);
        const isAmended = amendedSet.has(fileIdStr);
        const decision = decisions[fileIdStr];

        return (
          <AttachmentCard key={`${fileIdStr}-${idx}`} attachment={att} decision={decision} isAmended={isAmended} />
        );
      })}
    </div>
  );
}
